/**
 * Local-only Match Engine 2.5 visual gallery.
 * Available at /dev/me25 when NODE_ENV === 'development'. Not linked in nav; not for production.
 */
import React, { useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { createStore } from 'redux';
import styled from 'styled-components';
import PlayerCard from '../Team/components/StrategyContainer/components/GameFields 3.0/components/PlayerCard';
import CaptainPanel from '../Team/components/StrategyContainer/components/CaptainPanel';
import LiveMatchBar from '../Team/components/StrategyContainer/components/LiveMatchBar';
import StrategyToolbar from '../Team/components/StrategyContainer/components/StrategyToolbar';
import {
  LINEUP_FORBIDDEN_MESSAGE,
  EFFORT_LABELS,
  EFFORT_EXPLAINERS,
  EFFORT_FOOTNOTE,
  captainPreviewLabel,
  captainChangeLabel,
  SUSPENDED_HINT,
  formatPracticeBoostUntil,
} from '../../service/matchEngine';
import {
  YellowCountMark,
  SuspendedMark,
  SentOffMark,
  CaptainMark,
  InjuredMark,
  MatchEventMark,
  HappyMark,
  UnhappyMark,
  LoanInMark,
  LoanOutMark,
  ListedMark,
  ListedLoanMark,
  OfferMark,
  AcademyMark,
} from '../../components/MatchMarks';
import { fontUi } from '../../theme/typography';

const Page = styled.div`
  ${fontUi};
  min-height: 100vh;
  padding: 24px 20px 64px;
  background: ${({ theme }) => theme.page};
  color: ${({ theme }) => theme.text};
  max-width: 1100px;
  margin: 0 auto;
  h1 {
    font: 700 28px 'Clash Display', Archivo, sans-serif;
    margin: 0 0 6px;
  }
  .lede {
    margin: 0 0 20px;
    color: ${({ theme }) => theme.textFaint};
    font-size: 14px;
  }
  .banner {
    margin-bottom: 20px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff3cd;
    color: #664d03;
    font-size: 13px;
    border: 1px solid #ffecb5;
  }
  section {
    margin: 0 0 28px;
    padding: 16px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.cardRadius || '10px'};
    background: ${({ theme }) => theme.card};
  }
  h2 {
    margin: 0 0 4px;
    font-size: 16px;
  }
  .hint {
    margin: 0 0 14px;
    font-size: 12px;
    color: ${({ theme }) => theme.textFaint};
  }
  .grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
  }
  .sample {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 140px;
  }
  .sample label {
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.textFaint};
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 6px;
    background: ${({ theme }) => theme.hover};
    font-size: 12px;
  }
  .error {
    padding: 10px 12px;
    border-radius: 6px;
    background: #fdecea;
    color: #611a15;
    font-size: 13px;
  }
  .tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }
  .tabs button {
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.hover};
    color: inherit;
    border-radius: 6px;
    padding: 6px 10px;
    font: 600 12px Archivo, sans-serif;
    cursor: pointer;
  }
  .tabs button[data-on='true'] {
    background: ${({ theme }) => theme.text};
    color: ${({ theme }) => theme.card};
  }
  .pitch-bg {
    padding: 20px;
    border-radius: 8px;
    background: linear-gradient(180deg, #1a5c3a 0%, #0f3d28 100%);
  }
  ul.copy {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.45;
  }
`;

const basePlayer = (id, overrides = {}) => ({
  _id: id,
  first_name: overrides.first || 'Alex',
  last_name: overrides.last || 'Rivera',
  fav_position: overrides.pos || 'CM',
  current_position: overrides.slot || 'LCM',
  form_range: overrides.form_range ?? 2,
  rating_range: 'Strong',
  potential_range: 'World Class',
  age: 24,
  yellow_cards: overrides.yellow_cards,
  suspension: overrides.suspension,
  ...overrides,
});

const CAPTAIN_SCENARIOS = {
  active_high: {
    player: { _id: 'c1', name: 'Alex Rivera' },
    auto: false,
    change: { allowed: true, free: false, reason: 'season_change' },
    seasonChangesLeft: 1,
    preview: { status: 'active', backing: 'high' },
  },
  auto: {
    player: { _id: 'c1', name: 'Alex Rivera' },
    auto: true,
    change: { allowed: true, free: true, reason: 'first_pick' },
    seasonChangesLeft: 1,
    preview: { status: 'active', backing: 'mid' },
  },
  benched: {
    player: { _id: 'c1', name: 'Alex Rivera' },
    auto: false,
    change: { allowed: true, free: false, reason: 'season_change' },
    seasonChangesLeft: 0,
    preview: { status: 'benched', backing: null },
  },
  excused: {
    player: { _id: 'c1', name: 'Alex Rivera' },
    auto: false,
    change: { allowed: true, free: true, reason: 'long_injury' },
    seasonChangesLeft: 1,
    preview: { status: 'excused', backing: null },
  },
  vacated: {
    player: null,
    auto: false,
    change: { allowed: true, free: true, reason: 'vacated' },
    seasonChangesLeft: 1,
    preview: { status: 'vacated', backing: null },
  },
  locked_match: {
    player: { _id: 'c1', name: 'Alex Rivera' },
    auto: false,
    change: { allowed: false, free: false, reason: 'match_running' },
    seasonChangesLeft: 0,
    preview: { status: 'active', backing: 'low' },
  },
  none: {
    player: null,
    auto: false,
    change: { allowed: true, free: true, reason: 'first_pick' },
    seasonChangesLeft: 1,
    preview: { status: 'none', backing: null },
  },
};

const liveMatchWithCards = {
  _id: 'm1',
  minute: 67,
  home_team: { _id: 'club', name: 'Demo FC' },
  away_team: { _id: 'opp', name: 'Rivals United' },
  home_team_statistics: {
    goals: [
      { minute: 12, player: { _id: 'g1', name: 'Alex Rivera' } },
      { minute: 55, player: { _id: 'g2', name: 'Sam Cole' } },
    ],
    yellow_cards: [
      { minute: 22, player: { _id: 'y1', name: 'Sam Cole' } },
      { minute: 51, player: { _id: 'y2', name: 'Pat Ng' } },
    ],
    red_cards: [
      {
        minute: 64,
        second_yellow: true,
        player: { _id: 'sent', name: 'Lee Park' },
      },
    ],
  },
  away_team_statistics: {
    goals: [{ minute: 41, player: { _id: 'ag1', name: 'Rio Vale' } }],
    yellow_cards: [{ minute: 38, player: { _id: 'ay1', name: 'Jo West' } }],
    red_cards: [],
  },
  home_style: 'Wide',
  away_style: 'Central',
};

function buildDemoState(captainKey) {
  const captain = CAPTAIN_SCENARIOS[captainKey];
  const players = [
    basePlayer('c1', { first: 'Alex', last: 'Rivera', slot: 'LCM', pos: 'CM' }),
    basePlayer('gk1', { first: 'Kim', last: 'Goal', slot: 'GK', pos: 'GK' }),
    basePlayer('sus1', {
      first: 'Banned',
      last: 'Player',
      suspension: 1,
      yellow_cards: 0,
      form_range: 2,
    }),
    basePlayer('y2', { first: 'Two', last: 'Yellows', yellow_cards: 2 }),
    basePlayer('sent', {
      first: 'Lee',
      last: 'Park',
      slot: 'CF',
      yellow_cards: 1,
    }),
    basePlayer('inj', {
      first: 'Hurt',
      last: 'Knee',
      form_range: -1,
      suspension: 0,
    }),
  ];
  return {
    user: {
      isAuth: true,
      isPremium: true,
      team_info: { _id: 'club', name: 'Demo FC' },
    },
    team: {
      areUnsavedChangesAvailable: false,
      seasonNumber: 3,
      isPlayerLoading: false,
      myTeam: {
        academyList: [],
        playersListIds: [],
        allSquad: { players, seasonNumber: 3, academyPlayers: [] },
        loading: false,
      },
      strategy: {
        currentStrategy: '4-3-3',
        opponentStrategy: '4-4-2',
        isAutoFormatLoading: false,
        isAutoFormationEnabled: false,
        opponentName: 'Rivals United',
        allTeam: players,
        majorStaff: players.filter((p) => p.current_position),
        notMajorStaff: [],
        opponentSquad: [],
        upcomingCalendar: null,
        matchRunning: liveMatchWithCards,
        captain,
        hasCaptainField: true,
        formationStyles: {
          byFormation: {
            '4-4-2': 'Direct',
            '4-3-3': 'Wide',
            '4-2-3-1': 'Wide',
            '4-5-1': 'Central',
            '3-5-2': 'Central',
            '4-4-1-1': 'Direct',
          },
          beats: {
            Wide: 'Central',
            Central: 'Direct',
            Direct: 'Wide',
          },
        },
        hasFormationStyles: true,
        practiceBoostUntil: new Date(
          Date.now() + 6 * 60 * 60 * 1000
        ).toISOString(),
        hasPracticeBoostUntil: true,
        tactics: { mindset: 1, effort: 1 },
        teamKit: null,
        opponentKit: null,
        loading: false,
        setTeam: { loading: false, competeLoading: false },
        selectedPlayer: { index: null, type: null },
        empty: {},
        clickedPitchPlayerPosition: null,
        displayedSection: null,
        formationFilter: 'ALL',
        discordChannel: null,
        isOpponentPremium: false,
      },
      tips: { show: false },
    },
  };
}

function demoReducer(state = buildDemoState('active_high'), action) {
  if (action.type === 'DEMO_SET_CAPTAIN_SCENARIO') {
    return buildDemoState(action.payload);
  }
  if (action.type === 'set strategy captain block') {
    return {
      ...state,
      team: {
        ...state.team,
        strategy: {
          ...state.team.strategy,
          captain: action.payload,
          hasCaptainField: true,
        },
      },
    };
  }
  return state;
}

const CardSample = ({ label, ...props }) => (
  <div className="sample">
    <label>{label}</label>
    <div className="pitch-bg">
      <PlayerCard
        firstName={props.firstName || 'Alex'}
        lastName={props.lastName || 'Rivera'}
        ratingRange="Strong"
        potentialRange="World Class"
        formRange={props.formRange ?? 2}
        playerPos={props.playerPos || 'LCM'}
        _id={props._id || 'demo'}
        yellowCards={props.yellowCards}
        suspension={props.suspension}
        sentOff={props.sentOff}
        isCaptain={props.isCaptain}
        captainAuto={props.captainAuto}
      />
    </div>
  </div>
);

export default function DevMe25Gallery() {
  const [captainKey, setCaptainKey] = useState('active_high');
  const store = useMemo(() => {
    const s = createStore(demoReducer, buildDemoState(captainKey));
    // Swallow captain POSTs in the gallery.
    const originalDispatch = s.dispatch;
    s.dispatch = (action) => {
      if (typeof action === 'function') {
        return Promise.resolve({ ok: true });
      }
      return originalDispatch(action);
    };
    return s;
  }, []);

  React.useEffect(() => {
    store.dispatch({ type: 'DEMO_SET_CAPTAIN_SCENARIO', payload: captainKey });
  }, [captainKey, store]);

  const boostLabel = formatPracticeBoostUntil(
    new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString()
  );
  const scenario = CAPTAIN_SCENARIOS[captainKey];

  return (
    <Provider store={store}>
      <Page>
        <div className="banner">
          Local ME 2.5 gallery — <code>/dev/me25</code> only in development.
          Mock data; captain Save is a no-op.
        </div>
        <h1>Match Engine 2.5 — visual check</h1>
        <p className="lede">
          Pitch tokens, captain panel, live cards, styles, practice boost and
          copy. Toggle captain scenarios below.
        </p>

        <section>
          <h2>Pitch tokens (PlayerCard)</h2>
          <p className="hint">
            Card art for yellows / red / second yellow; injured well matches
            squad status icons; only captain uses the letter C (C* when auto).
          </p>
          <div className="grid">
            <CardSample label="Normal" _id="n1" />
            <CardSample label="1 yellow" _id="n2" yellowCards={1} />
            <CardSample label="2 yellows" _id="n3" yellowCards={2} />
            <CardSample
              label="Suspended"
              _id="n4"
              suspension={1}
              firstName="Banned"
              lastName="Player"
            />
            <CardSample
              label="Sent off"
              _id="n5"
              sentOff
              yellowCards={1}
              firstName="Lee"
              lastName="Park"
            />
            <CardSample
              label="Injured"
              _id="n6"
              formRange={-1}
              firstName="Hurt"
              lastName="Knee"
            />
            <CardSample label="Captain" _id="n7" isCaptain />
            <CardSample
              label="Captain auto"
              _id="n8"
              isCaptain
              captainAuto
            />
            <CardSample
              label="Captain + 1 yellow"
              _id="n9"
              isCaptain
              yellowCards={1}
            />
          </div>
        </section>

        <section>
          <h2>Status marks (squad / table)</h2>
          <p className="hint">
            Same marks as likes/loan wells — {SUSPENDED_HINT}
          </p>
          <div className="chips">
            <span className="chip">
              <YellowCountMark count={1} size={16} /> one yellow
            </span>
            <span className="chip">
              <YellowCountMark count={2} size={16} /> two yellows
            </span>
            <span className="chip">
              <SuspendedMark size={16} /> suspended
            </span>
            <span className="chip">
              <SentOffMark size={16} /> straight red
            </span>
            <span className="chip">
              <SentOffMark secondYellow size={16} /> second yellow
            </span>
            <span className="chip">
              <InjuredMark size={18} /> injured
            </span>
            <span className="chip">
              <CaptainMark size={18} /> captain
            </span>
            <span className="chip">
              <CaptainMark auto size={18} /> auto captain
            </span>
            <span className="chip">
              <HappyMark size={18} /> happy
            </span>
            <span className="chip">
              <UnhappyMark size={18} /> unhappy
            </span>
            <span className="chip">
              <LoanInMark size={18} /> loan in
            </span>
            <span className="chip">
              <LoanOutMark size={18} /> loan out
            </span>
            <span className="chip">
              <ListedMark size={18} /> listed
            </span>
            <span className="chip">
              <ListedLoanMark size={18} /> listed loan
            </span>
            <span className="chip">
              <OfferMark size={18} /> offer
            </span>
            <span className="chip">
              <AcademyMark size={18} /> academy
            </span>
          </div>
        </section>

        <section>
          <h2>Captain panel</h2>
          <p className="hint">
            Real <code>CaptainPanel</code> against mock Redux. Switch scenarios
            to see preview / change / auto copy.
          </p>
          <div className="tabs">
            {Object.keys(CAPTAIN_SCENARIOS).map((key) => (
              <button
                key={key}
                type="button"
                data-on={String(captainKey === key)}
                onClick={() => setCaptainKey(key)}
              >
                {key}
              </button>
            ))}
          </div>
          <p className="hint">
            Preview: {captainPreviewLabel(scenario.preview) || '—'} · Change:{' '}
            {captainChangeLabel(scenario.change, scenario.seasonChangesLeft) ||
              '—'}
          </p>
          <CaptainPanel />
        </section>

        <section>
          <h2>Live match bar (goals + cards)</h2>
          <p className="hint">
            Ball / card art with minutes from team statistics (not letter chips).
          </p>
          <div className="chips" style={{ marginBottom: 12 }}>
            <span className="chip">
              <MatchEventMark kind="goal" minute={12} playerName="Alex Rivera" />
            </span>
            <span className="chip">
              <MatchEventMark kind="yellow" minute={22} playerName="Sam Cole" />
            </span>
            <span className="chip">
              <MatchEventMark
                kind="red"
                minute={64}
                secondYellow
                playerName="Lee Park"
              />
            </span>
          </div>
          <LiveMatchBar
            match={liveMatchWithCards}
            discordChannel="demo"
            isAuthenticated={false}
          />
        </section>

        <section>
          <h2>Strategy toolbar — styles, effort, practice boost</h2>
          <p className="hint">
            Formation options include style labels when formationStyles is
            present. Hover Effort for explainers.
          </p>
          <StrategyToolbar />
          <p className="hint" style={{ marginTop: 12 }}>
            Practice boost sample: active until <strong>{boostLabel}</strong>
          </p>
          <ul className="copy">
            {EFFORT_LABELS.map((label) => (
              <li key={label}>
                <strong>{label}:</strong> {EFFORT_EXPLAINERS[label]}
              </li>
            ))}
            <li>{EFFORT_FOOTNOTE}</li>
          </ul>
        </section>

        <section>
          <h2>Lineup refused (403 copy)</h2>
          <div className="error">{LINEUP_FORBIDDEN_MESSAGE}</div>
        </section>

        <section>
          <h2>Favourite style vs formation</h2>
          <p className="hint">
            When <code>fav_style</code> is on the player payload, insights /
            filters show style. Otherwise favourite formation stays.
          </p>
          <div className="chips">
            <span className="chip">
              fav_style present → Favourite style: Wide / Central / Direct
            </span>
            <span className="chip">
              fav_style missing → Favourite formation: 4-3-3 …
            </span>
          </div>
        </section>
      </Page>
    </Provider>
  );
}
